/**
 * Camera list row types aligned with CameraSerializer list/retrieve payload.
 *
 * Guaranteed by backend (Camera model + CameraSerializer):
 * - id, camera_esn, is_active, organization (name), created_at, status
 *
 * Nullable / optional in the API:
 * - description (TextField blank/null)
 * - direction (blank/null)
 * - status.correlation_window_seconds (null when no correlation settings)
 * - status.device_health (null when no Device Health snapshot)
 */
export type CameraListStatusView = {
    analytics_configured: boolean;
    enabled_analytics_count: number;
    enabled_analytics: { analytics_key: string; name?: string }[];
    vehicle_detection_enabled: boolean;
    correlation_window_seconds: number | null;
    device_health: {
        primary_status?: string | null;
        connection_status?: string | null;
        mapping_status?: string | null;
    } | null;
};

export type CameraListRow = {
    id: number;
    camera_esn: string;
    organization?: string;
    direction?: string | null;
    created_at?: string;
    /**
     * Camera Name in the Super User View Camera table (C-02/C-03).
     * API/model field remains ``description``; may be null/blank.
     */
    description?: string | null;
    is_active: boolean;
    _toggling?: boolean;
    status: CameraListStatusView;
};

/**
 * View Camera table header order (8 columns). Camera Name sits immediately
 * after S.No. and is sourced from ``record.description``.
 */
export const VIEW_CAMERA_TABLE_HEADERS = [
    'S.No',
    'Camera Name',
    'Camera ESN',
    'Organization',
    'Direction',
    'Created At',
    'Status',
    'Action Items',
] as const;

export function cameraExpandAriaLabel(expanded: boolean): string {
    return expanded ? 'Collapse camera details' : 'Expand camera details';
}

export function nextExpandedCameraId(
    currentExpandedId: number | null,
    clickedCameraId: number | null | undefined,
): number | null {
    if (clickedCameraId == null) {
        return currentExpandedId;
    }
    return currentExpandedId === clickedCameraId ? null : clickedCameraId;
}

export function formatOptionalStatusValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || String(value).trim() === '') {
        return '—';
    }
    return String(value);
}

/** Display value for the Camera Name column; empty/whitespace → em dash. */
export function formatCameraNameDisplay(description: string | null | undefined): string {
    return formatOptionalStatusValue(description);
}

/** Correlation window for display: uses shared empty formatting, appends unit when present. */
export function formatCorrelationWindowSeconds(value: number | null | undefined): string {
    const formatted = formatOptionalStatusValue(value);
    return formatted === '—' ? formatted : `${formatted}s`;
}

export function formatEnabledAnalyticsList(camera: CameraListRow): string {
    const names = (camera.status?.enabled_analytics || [])
        .map((item) => item.name || item.analytics_key)
        .filter(Boolean);
    if (!names.length) {
        return 'None';
    }
    return names.join(', ');
}

export function cameraDeviceHealthUnavailable(camera: CameraListRow): boolean {
    return camera.status?.device_health == null;
}
